/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable react-hooks/exhaustive-deps */
import {
  CheckCircleFilled,
  CloseCircleOutlined,
  ExportOutlined,
  RocketOutlined,
  HourglassOutlined,
  LoadingOutlined,
  // MonitorOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  PlusCircleOutlined,
  SearchOutlined,
  SettingOutlined,
  SyncOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Badge,
  Button,
  Divider,
  message,
  Modal,
  Tabs,
  Tag,
  Tooltip,
} from 'antd';
import AnimatedNumber from 'components/animatedNumber';
import BacklinksHistory from 'components/backlinksHistory';
// import BacklinksMonitoring from 'components/backlinksMonitoring';
import BrowseBacklinksOpportunities from 'components/browseBacklinksOpportunities';
import DomainRating from 'components/domainRating';
import DomainSettingsModal from 'components/domainSettingsModal';
import FastResponderBadge from 'components/fastResponderBadge';
import Header from 'components/header';
import InviteYourFriends from 'components/inviteYourFriends';
import KarmaIcon from 'components/karmaIcon';
import ModalPointsToAllocate from 'components/modalPointsToAllocate';
import Onboarding from 'components/onboarding/index.js';
import RemainingAvailableRequests from 'components/remainingAvailableRequests';
import { shareKarmaLinksModalFunction } from 'components/shareKarmaLinksModalFunction';
import WelcomePanel from 'components/welcomePanel';
import moment from 'moment';
import { useContext, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  deleteRejectedBacklink,
  getDetails,
  resumeDomain,
} from 'services/api/domains';
import { redeemPoints, sawOnboarding } from 'services/api/users';
import { allocatePoints } from 'services/api/domains';
import DomainContext from 'services/domainContext';
import ProfileContext from 'services/profileContext';
import styles from 'styles/dashboard.module.scss';
import { BUY_POINTS_URL, LOCAL_STORAGE } from 'utils/constants';
import { formatResponseRate } from 'utils/utils';
import { usePostHogFeatureFlag } from 'hooks/usePostHogFeatureFlag';
import {
  isDrMissing,
  isPlanStepDone,
  isRejectedValidation,
} from 'utils/profileValidation';
import VerifyDomainBanner from 'components/verifyDomainBanner';

const Vignette = ({
  name,
  figure,
  featured = false,
  animate = false,
  extra,
}) => (
  <div
    className={`${styles.vignetteWrapper}${
      featured ? ` ${styles.vignetteFeatured}` : ''
    }`}
  >
    {extra && <div className={styles.extra}>{extra}</div>}
    <div className={styles.vignetteFigure}>
      {animate ? <AnimatedNumber value={figure} /> : figure}
    </div>
    <div className={styles.vignetteName}>{name}</div>
  </div>
);

const Dashboard = () => {
  const proPlansEnabled = usePostHogFeatureFlag('pro-plan');
  const redeemHandledRef = useRef(false);
  const [forceOnboarding, setForceOnboarding] = useState(false);
  const [isInfoResponsivityOpen, setIsInfoResponsivityOpen] = useState(false);

  const [newDomainFlow, setNewDomainFlow] = useState(false);
  const { profile, updateProfile } = useContext(ProfileContext);
  const { domains, updateDomains } = useContext(DomainContext);
  const autoAllocatedRef = useRef(null);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [domainDetailsLoading, setDomainDetailsLoading] = useState(false);
  const [domainDetails, setDomainDetails] = useState(null);
  const [settingsModalisOpen, setSettingsModalisOpen] = useState(false);
  const [removeLoading, setRemoveLoading] = useState(false);
  const [showWelcomePanel, setShowWelcomePanel] = useState(false);
  const [tabQuery, setTabQuery] = useState('browse');
  const [searchParams, setSearchParams] = useSearchParams();
  const [loadingResume, setLoadingResume] = useState(false);

  const getDomainDetails = () => {
    setDomainDetailsLoading(true);
    return getDetails({ name: selectedDomain.name })
      .then(({ data }) => {
        const ddetails = {
          ...data,
          sentBacklinksCount: data.requests?.filter(
            (e) => e.status === 'DONE' && e.from === data._id
          ).length,
          reveals: domains.find((d) => d.name === data.name)?.reveals,
        };
        setDomainDetails(ddetails);
      })
      .catch((e) => {
        console.error(e);
        message.error('Error while loading domain details');
      })
      .finally(() => {
        setDomainDetailsLoading(false);
      });
  };

  const openNewDomainFlow = () => {
    setNewDomainFlow(false);
    localStorage.removeItem(LOCAL_STORAGE.CURRENT_DOMAIN_VALIDATION);
    setTimeout(() => {
      //reset localstorage
      setNewDomainFlow(true);
    });
  };

  useEffect(() => {
    const profileNotValidated =
      !profile?.emailVerified || !profile?.linkedinProfile;

    const needsDomainSetup = !domains || domains.length === 0;
    const showRejected = isRejectedValidation(profile, domains);
    // A new member (nothing listed yet) still owes the plan step, even if
    // LinkedIn was already connected (LinkedIn sign-up).
    const needsPlanStep =
      !isPlanStepDone(profile) &&
      !domains?.some((d) => d.status === 'APPROVED');

    setTimeout(() => {
      setForceOnboarding(
        profileNotValidated ||
          needsDomainSetup ||
          needsPlanStep ||
          showRejected
      );
    }, 500);

    // test if current selected domain is in the list of domains, if not, select the first one
    if (
      domains &&
      domains.length > 0 &&
      selectedDomain &&
      !domains.find((d) => d.name === selectedDomain.name)
    ) {
      setSelectedDomain(domains[0]);
    } else if (domains && domains.length > 0 && !selectedDomain) {
      // First domain was just added (onboarding closed): show it.
      setSelectedDomain(domains[0]);
    } else if (selectedDomain && selectedDomain.name) {
      getDomainDetails();
    }
  }, [domains, profile]);

  useEffect(() => {
    const domainQuery = searchParams.get('domain');
    const domainQuerySelect =
      domainQuery && domainQuery.length > 0
        ? domains?.find((d) => d.name === domainQuery)
        : null;
    const firstDomain = domains && domains.length > 0 ? domains[0] : null;
    setSelectedDomain(domainQuerySelect || firstDomain);
    const tabQueryParam = searchParams.get('tab');
    if (tabQueryParam && tabQueryParam.length > 0) {
      if (tabQueryParam === 'received') {
        setTabQuery('history');
      } else {
        setTabQuery(tabQueryParam);
      }
    }
  }, [searchParams]);

  useEffect(() => {
    const action = searchParams.get('action');
    const domain = searchParams.get('domain');

    if (action === 'redeem' && domain && !redeemHandledRef.current) {
      redeemHandledRef.current = true;
      redeemPoints(domain)
        .then(({ data }) => {
          if (data.pointsRedeemed) {
            message.success("You've received 50 points!");
          } else {
            message.info('Points already redeemed');
          }
        })
        .catch((e) => {
          if (e.response?.data?.error === 'DOMAIN_NOT_FOUND') {
            message.error('Domain not found');
          } else {
            message.error('Failed to redeem points');
          }
        })
        .finally(() => {
          setSearchParams((prev) => {
            prev.delete('action');
            return prev;
          });
        });
    }
  }, []);

  useEffect(() => {
    if (selectedDomain && selectedDomain.name) {
      getDomainDetails();
    }
  }, [selectedDomain]);

  useEffect(() => {
    setShowWelcomePanel(!profile.hasSeenOnboarding);
  }, [profile]);

  // Pro's monthly karma lands on the user, not the domain. With
  // a single domain, allocate it straight away so locked cards unlock.
  useEffect(() => {
    const amount = profile?.pointsToAllocate || 0;
    if (
      amount <= 0 ||
      !profile?.linkedinProfile ||
      domains?.length !== 1 ||
      autoAllocatedRef.current === amount
    ) {
      return;
    }
    autoAllocatedRef.current = amount;
    allocatePoints({ amount, name: domains[0].name })
      .then(() => {
        message.success(`${amount} Karma Points added to ${domains[0].name}`);
        updateDomains();
        updateProfile({ silent: true });
      })
      .catch((e) => {
        // The allocate modal stays available as a fallback.
        console.error(e);
      });
  }, [profile?.pointsToAllocate, domains?.length]);

  const closeOnboarding = () => {
    setShowWelcomePanel(false);
    sawOnboarding().catch((e) => {
      console.error(e);
    });
  };

  useEffect(() => {
    const isInfoResponse = localStorage.getItem(
      LOCAL_STORAGE.INFO_RESPONSIVITY
    );
    if (isInfoResponse && isInfoResponse.length > 0) {
      setIsInfoResponsivityOpen(isInfoResponse === 'true');
    } else {
      setIsInfoResponsivityOpen(true);
    }
  }, []);

  const openSettingsModal = () => setSettingsModalisOpen(true);
  const closeSettingsModal = () => setSettingsModalisOpen(false);

  const getDomainStatusIcon = (domain, status) => {
    if (!domain.live) {
      return <PauseCircleOutlined style={{ marginRight: 4 }} />;
    }
    switch (status) {
      default:
      case 'TO_RE_REVIEW':
      case 'TO_REVIEW':
        return (
          <HourglassOutlined
            twoToneColor="#051200"
            className={styles.iconStatus}
          />
        );
      case 'APPROVED':
        return <CheckCircleFilled className={styles.iconStatus} />;
      case 'REJECTED':
      case 'BANNED':
        return <CloseCircleOutlined className={styles.iconStatus} />;
    }
  };

  const removeDomain = () => {
    setRemoveLoading(true);
    deleteRejectedBacklink({ name: domainDetails.name })
      .then(() => {
        //list domains
        //select first one
        return updateDomains().then(() => {
          setSelectedDomain(domains.length > 0 ? domains[0] : null);
        });
      })
      .catch((e) => {
        console.error(e);
        message.error('Error while deleting domain, please try again');
      })
      .finally(() => {
        setRemoveLoading(false);
      });
  };

  const onChangeTab = (activekey) => {
    setSearchParams((prev) => {
      prev.set('tab', activekey);
      return prev;
    });
  };

  const resumeDomainFunction = () => {
    if (loadingResume) {
      return;
    }
    setLoadingResume(true);
    resumeDomain({
      name: domainDetails.name,
    })
      .then(() => {
        message.success(`${domainDetails.name} was resumed successfully`);
        updateDomains();
        return getDomainDetails();
      })
      .catch((e) => {
        console.error(e);
        message.error(
          'There was a problem when trying to resume the domain, please refresh your page an try again.'
        );
      })
      .finally(() => {
        setLoadingResume(false);
      });
  };

  const getRejectionText = () => {
    switch (domainDetails.rejectionDetails.reason) {
      default:
      case 'DOMAIN_ALREADY_EXISTS':
        return 'This domain has already been registered on KarmaLinks';
      case 'LOWER_THAN_MIN_DR':
        return 'Your domain rating is too low to enter KarmaLinks for now';
      case 'FORBIDDEN_CATEGORY':
        return 'This website category is not currently supported on KarmaLinks';
      case 'OTHER':
        return 'This domain was not approved by KarmaLinks';
    }
  };
  const closeInfoResposivity = () => {
    setIsInfoResponsivityOpen(false);
    localStorage.setItem(LOCAL_STORAGE.INFO_RESPONSIVITY, false);
  };

  const hasAtLeastOneDomainApproved =
    domains?.filter((d) => d.status === 'APPROVED').length > 0;

  const pendingRequests = domainDetails?.requests?.filter(
    (e) =>
      (e.status === 'PENDING' && e.initiatedBy !== domainDetails._id) ||
      (e.status === 'ACCEPTED' && e.from === domainDetails._id)
  );

  return (
    <div
      className={`${
        showWelcomePanel && hasAtLeastOneDomainApproved
          ? ` ${styles.welcomePanelWrapper}`
          : ''
      }`}
    >
      {showWelcomePanel && hasAtLeastOneDomainApproved && (
        <WelcomePanel close={closeOnboarding} />
      )}
      <Header />
      <>
        <div className={`${styles.wrapper}`}>
          <div className={styles.inner}>
            <InviteYourFriends />
            <ModalPointsToAllocate />
            <div className={styles.domainsTitle}>
              {`Domains${
                domains && domains.length > 0 ? ` (${domains.length})` : ''
              }`}
            </div>
            <div className={styles.domainsListWrapper}>
              {domains?.map((d) => {
                const numberRequestPending = d?.requests?.filter(
                  (e) =>
                    (e.status === 'PENDING' && e.initiatedBy !== d._id) ||
                    (e.status === 'ACCEPTED' && e.from === d._id)
                ).length;

                return (
                  <Badge
                    count={numberRequestPending}
                    key={d._id}
                    offset={[-18, 3]}
                  >
                    <Tag
                      onClick={() => {
                        setSearchParams((prev) => {
                          prev.set('domain', d.name);
                          prev.delete('onlyYC');
                          prev.delete('categories');
                          prev.delete('onlyGoodMatch');
                          prev.delete('onlyAvailable');
                          prev.delete('hideSlowResponders');
                          prev.delete('onlyNew');
                          prev.delete('sort');
                          prev.delete('sortDirection');
                          return prev;
                        });
                      }}
                      color={
                        d.status === 'TO_REVIEW' || d.status === 'TO_RE_REVIEW'
                          ? ''
                          : d.status === 'REJECTED' || d.status === 'BANNED'
                          ? 'red'
                          : 'green'
                      }
                      className={`${styles.domainTag} ${
                        selectedDomain?._id === d._id ? styles.selectedTag : ''
                      }${
                        d.status === 'TO_REVIEW' || d.status === 'TO_RE_REVIEW'
                          ? ` ${styles.domainNotLive}`
                          : ''
                      }${
                        d.status === 'REJECTED' || d.status === 'BANNED'
                          ? ` ${styles.domainRejected}`
                          : ''
                      }`}
                      key={d._id}
                    >
                      {selectedDomain?._id === d._id && domainDetailsLoading ? (
                        <LoadingOutlined spin style={{ marginRight: 4 }} />
                      ) : (
                        getDomainStatusIcon(d, d.status)
                      )}
                      {d.name}
                      {d.isYC && (
                        <img
                          src="/img/yc.png"
                          alt="ycombinator"
                          className={styles.ycLogoDomainName}
                        />
                      )}
                      {['APPROVED', 'TO_REVIEW'].includes(d.status) &&
                        d.points != null && (
                        <span
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <Divider
                            type="vertical"
                            style={{
                              borderColor: '#00000024',
                              height: '1.3em',
                              marginLeft: 0,
                              marginRight: '6px',
                            }}
                          />
                          {Math.round(d.points)}&nbsp;
                          <KarmaIcon />
                        </span>
                      )}
                    </Tag>
                  </Badge>
                );
              })}
              <Tag
                color="blue"
                className={`${styles.domainTag} ${styles.domainTagAdd}`}
                key={'add'}
                onClick={openNewDomainFlow}
              >
                <PlusCircleOutlined />
                &nbsp;Add a domain
              </Tag>
            </div>
            {domainDetails && (
              <div className={styles.detailsWrapper}>
                {!domainDetails.live && (
                  <Alert
                    message="Domain is paused"
                    description={
                      <div>
                        Your domain can't receive backlinks right now and
                        you can't ask backlinks either.
                      </div>
                    }
                    type="warning"
                    icon={<PauseCircleOutlined />}
                    showIcon
                    style={{ border: 'none', marginBottom: 12 }}
                    action={
                      <Button
                        size="small"
                        type="primary"
                        loading={loadingResume}
                        onClick={() => {
                          resumeDomainFunction();
                        }}
                        icon={<PlayCircleOutlined />}
                      >
                        Resume domain
                      </Button>
                    }
                  />
                )}
                {domainDetails.status !== 'REJECTED' &&
                  domainDetails.status !== 'BANNED' && (
                    <div className={styles.settingsContainer}>
                      <div className={styles.settingsWrapper}>
                        {domainDetails.isFastResponder && (
                          <div>
                            <Tooltip title="You are a fast responder. Others can see your badge on your domain details. You're more likely to receive requests now.">
                              <FastResponderBadge
                                style={{ marginRight: 12 }}
                              />
                              &nbsp;
                            </Tooltip>
                          </div>
                        )}
                        {domainDetails.live && (
                          <RemainingAvailableRequests
                            pendingGiveRequestsCount={
                              domainDetails.pendingGiveRequestsCount
                            }
                          />
                        )}
                        <div>
                          <Button
                            type="link"
                            onClick={openSettingsModal}
                            icon={<SettingOutlined />}
                          >
                            {`Settings for ${domainDetails.name}`}
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

                {domainDetails.status === 'REJECTED' && (
                  <>
                    <div className={styles.rejectedWrapper}>
                      <div className={styles.rejectedInner}>
                        <div style={{ flex: 1 }}>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                            }}
                          >
                            <CloseCircleOutlined
                              twoToneColor={'red'}
                              className={styles.rejectedIcon}
                            />
                            <div style={{ flexGrow: 1 }}>
                              <h2>{getRejectionText()}</h2>
                              <p>The domain has been rejected.</p>
                            </div>
                          </div>
                        </div>

                        <div className={styles.rejectedCta}>
                          <Button
                            onClick={removeDomain}
                            loading={removeLoading}
                          >
                            Remove Domain
                          </Button>
                        </div>
                      </div>
                    </div>
                    {domainDetails.rejectionDetails?.reason ===
                      'LOWER_THAN_MIN_DR' && (
                      <div style={{ marginTop: 24 }}>
                        For KarmaLinks to be effective, a minimum domain
                        rating is needed. This ensures every member gets
                        quality traffic from each backlink posted to the
                        community. If you don't have another domain and this
                        is a new website, consider increasing your domain
                        rating first.
                        <div
                          style={{
                            marginTop: 12,
                            padding: '12px 18px',
                            background: '#fffbe6',
                            borderRadius: 10,
                            color: '#614700',
                          }}
                        >
                          New website? Consider listing your site on relevant
                          directories and building quality content to
                          increase your domain rating.
                        </div>
                      </div>
                    )}
                  </>
                )}
                {domainDetails.status === 'BANNED' && (
                  <div className={styles.rejectedWrapper}>
                    <div className={styles.rejectedInner}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                        }}
                      >
                        <CloseCircleOutlined
                          twoToneColor={'red'}
                          className={styles.rejectedIcon}
                        />
                        <div style={{ flexGrow: 1 }}>
                          <h2>
                            This domain has been banned from KarmaLinks
                          </h2>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {domainDetails.status !== 'REJECTED' &&
                  domainDetails.status !== 'BANNED' && (
                  <div>
                    {!domainDetails.isFastResponder &&
                      isInfoResponsivityOpen && (
                        <div>
                          <Alert
                            style={{
                              background: '#fffbe3',
                              border: '1px solid #e9e4c5',
                            }}
                            closable
                            onClose={() => {
                              closeInfoResposivity();
                            }}
                            message={
                              <div>
                                <FastResponderBadge
                                  style={{ marginRight: 12 }}
                                />
                                <b>Fast Responder Badge</b> Reply within 48
                                hours to earn this badge and get 70% more
                                backlinks.
                              </div>
                            }
                          />
                        </div>
                      )}
                    <div className={styles.vignetteList}>
                      <Vignette
                        animate
                        figure={Math.round(domainDetails.points || 0)}
                        name={
                          <span>
                            Karma Points&nbsp;
                            <KarmaIcon />
                          </span>
                        }
                        featured
                        extra={
                          <div>
                            <Button
                              icon={<PlusCircleOutlined />}
                              style={{ padding: 0, paddingRight: 12 }}
                              type="link"
                              onClick={() => {
                                let modalInstance;
                                modalInstance = Modal.info({
                                  okButtonProps: { type: 'link' },
                                  width: 460,
                                  icon: null,
                                  closable: true,
                                  footer: null,
                                  title: 'Get more points',
                                  content: (
                                    <div>
                                      <div>
                                        <div className={styles.step}>
                                          <div className={styles.stepTitle}>
                                            1. Give backlinks
                                          </div>
                                          <div
                                            className={
                                              styles.stepDescription
                                            }
                                          >
                                            Explore available opportunities
                                            in the{' '}
                                            <b>
                                              Browse baclinks Opportunities
                                            </b>{' '}
                                            tab and click <b>Give</b> to
                                            earn Karma Points <KarmaIcon />
                                          </div>
                                        </div>
                                        <div className={styles.step}>
                                          <div className={styles.stepTitle}>
                                            2. Share on Linkedin
                                          </div>
                                          <div
                                            className={
                                              styles.stepDescription
                                            }
                                          >
                                            Get 100 <KarmaIcon /> KarmaPoints
                                            for every person who signs up
                                            using your unique referral link.
                                            <Button
                                              type="link"
                                              style={{
                                                fontWeight: 'bold',
                                              }}
                                              onClick={() =>
                                                shareKarmaLinksModalFunction(
                                                  profile.inviteCode
                                                )
                                              }
                                            >
                                              Learn more
                                            </Button>
                                          </div>
                                        </div>
                                        <div className={styles.step}>
                                          <div className={styles.stepTitle}>
                                            {proPlansEnabled
                                              ? '3. Get KarmaLinks Pro'
                                              : '3. Purchase points'}
                                          </div>
                                          <div
                                            className={
                                              styles.stepDescription
                                            }
                                          >
                                            <div
                                              style={{
                                                display: 'flex',
                                                justifyContent:
                                                  'space-between',
                                                alignItems: 'center',
                                              }}
                                            >
                                              <div>
                                                {proPlansEnabled
                                                  ? 'Get 200 bonus KarmaPoints every month with Pro.'
                                                  : 'Need more points instantly?'}
                                              </div>
                                              <div>
                                                {proPlansEnabled ? (
                                                  <Button
                                                    type="primary"
                                                    style={{
                                                      color: 'white',
                                                    }}
                                                    onClick={() => {
                                                      modalInstance?.destroy();
                                                      window.location.hash = '#/plan';
                                                    }}
                                                  >
                                                    <RocketOutlined />
                                                    &nbsp;Upgrade to Pro
                                                  </Button>
                                                ) : (
                                                  <a
                                                    href={BUY_POINTS_URL}
                                                    rel="noreferrer noopener"
                                                    target="_blank"
                                                  >
                                                    <Button
                                                      type="primary"
                                                      style={{
                                                        color: 'white',
                                                      }}
                                                    >
                                                      <ExportOutlined />
                                                      &nbsp;Purchase points
                                                    </Button>
                                                  </a>
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  ),
                                });
                              }}
                            >
                              Get more points
                            </Button>
                          </div>
                        }
                      />
                      {isDrMissing(domainDetails) ? (
                        <Vignette
                          figure="—"
                          name="Domain Rating"
                          extra={
                            <div className={styles.domainRatingExtra}>
                              Fetching
                            </div>
                          }
                        />
                      ) : (
                        domainDetails.metrics && (
                        <Vignette
                          figure={
                            <DomainRating
                              value={domainDetails.metrics?.authority?.dr}
                            />
                          }
                          name={'Domain Rating'}
                          extra={
                            domainDetails.metrics.date ? (
                              <Tooltip title="The domain rating is updated automatically.">
                                <div className={styles.domainRatingExtra}>
                                  <SyncOutlined />
                                  &nbsp;
                                  {`Synced on  ${moment(
                                    domainDetails.metrics.date
                                  ).format('Do MMM YYYY')}`}
                                </div>
                              </Tooltip>
                            ) : (
                              <></>
                            )
                          }
                        />
                        )
                      )}
                      {domainDetails.responseRate !== undefined && (
                        <Vignette
                          figure={
                            <Tooltip
                              title={
                                domainDetails.responseRate == null
                                  ? 'Not enough data — fewer than 3 resolved inbound requests.'
                                  : 'Share of inbound requests you responded to (accepted, completed, or declined). Pending and withdrawn requests are excluded.'
                              }
                            >
                              <span
                                style={
                                  domainDetails.responseRate == null
                                    ? { fontSize: 20, fontWeight: 600 }
                                    : undefined
                                }
                              >
                                {formatResponseRate(
                                  domainDetails.responseRate
                                )}
                              </span>
                            </Tooltip>
                          }
                          name="Response Rate"
                        />
                      )}
                      <Vignette
                        figure={domainDetails?.receivedBacklinksCount || 0}
                        name={`Received Backlinks (${
                          domainDetails?.sentBacklinksCount || 0
                        } sent)`}
                      />
                    </div>
                    <div>
                      <VerifyDomainBanner
                        domain={{
                          ...domainDetails,
                          claimed: domains?.find(
                            (d) => d.name === domainDetails.name
                          )?.claimed,
                        }}
                      />
                      <Tabs
                        onChange={onChangeTab}
                        activeKey={tabQuery}
                        items={[
                          domainDetails.live &&
                          ['APPROVED', 'TO_REVIEW'].includes(
                            domainDetails.status
                          )
                            ? {
                                key: 'browse',
                                label: `Browse backlinks opportunities`,
                                children: (
                                  <BrowseBacklinksOpportunities
                                    domain={domainDetails}
                                    refreshDomainDetails={getDomainDetails}
                                  />
                                ),
                                icon: <SearchOutlined />,
                              }
                            : {},
                          {
                            key: 'history',
                            label:
                              pendingRequests.length > 0 ? (
                                <Badge
                                  count={pendingRequests.length}
                                  offset={[0, -5]}
                                >
                                  History of requests and backlinks
                                </Badge>
                              ) : (
                                'History of requests and backlinks'
                              ),
                            children: (
                              <BacklinksHistory
                                domain={domainDetails}
                                refreshDomainDetails={getDomainDetails}
                              />
                            ),
                            icon: <UnorderedListOutlined />,
                          },
                          // {
                          //   key: 'monitoring',
                          //   label: `Backlinks Monitoring`,
                          //   children: <BacklinksMonitoring />,
                          //   icon: <MonitorOutlined />,
                          // },
                        ]}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        {(forceOnboarding || newDomainFlow) && (
          <Onboarding
            firstOnboarding={forceOnboarding}
            close={() => {
              localStorage.removeItem(
                LOCAL_STORAGE.CURRENT_DOMAIN_VALIDATION
              );
              setNewDomainFlow(false);
              setForceOnboarding(false);
            }}
          />
        )}
        {domainDetails && settingsModalisOpen && (
          <DomainSettingsModal
            isOpen={settingsModalisOpen}
            onClose={closeSettingsModal}
            domain={domainDetails}
            getDomainDetails={getDomainDetails}
          />
        )}
      </>
    </div>
  );
};

export default Dashboard;
